/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { useDelta } from 'react-delta';
import { useApolloClient } from '@apollo/client';
import { zip } from 'lodash';

import {
  FacetData,
  GenericResultData,
  HistogramData,
  LogEntryData,
  LogEntryEdge,
  PlotData,
  PqResultData,
} from 'common/graphqlTypes';
import { DEFAULT_PAGINATED_DATA, getCachedPaginatedData } from 'common/util/lrqRequest';
import { ErrorResult, EventQueryInput } from 'hooks/useLRQ/useLRQ';
import { generateAllDataNode, generateLoglineNode } from 'hooks/useLRQ/useLRQ.queries';
import { activeSearchMatchCountState, eventCacheWatcher, lrqErrorsById } from 'api/apolloClient.state';
import { eventSortOrderState } from 'components/search/SearchLogsPage/SearchStateManager.state';

export type EventCacheData = {
  data: FacetData & GenericResultData & HistogramData & LogEntryData & PlotData & PqResultData;
  isDone: boolean;
  facetData: FacetData;
  histogramData: HistogramData;
  logEntryData: LogEntryData;
  plotData: PlotData;
  pqResultData: PqResultData;
  queryVariables: EventQueryInput;
  seriesData?: any;
};

export type PlaceholderLogEntryEdge = {
  notLoaded: boolean; // use to distiguish between non-loaded `PlaceholderLogEntryEdge` and loaded `LogEntryEdge`
  continuationToken: string; // placeholder LogEntryEdge represented by it's index in the currentLogEntryEdges array
  logEntry: null;
};

/**
 * PaginatedData represents derived state from multiple LOG requests with the same pagination context. In other words,
 * as the user scrolls through a large set of data, the client will dispatch additional continuation LOG requests to
 * get the additional events to display.
 *
 * Most importantly, PaginatedData includes currentLogEntryEdges, which represents all of those events from different
 * requests stitched together in order with placeholders to represent events that have not yet loaded. Other metadata is
 * included, such as whether the first event in the results set has been loaded.
 */
export type PaginatedData = {
  currentIds: string[];
  currentLogEntryEdges: Array<LogEntryEdge | PlaceholderLogEntryEdge>;
  cursors: Array<LogEntryEdge>;
  includesFirstLog: boolean;
  includesLastLog: boolean;
  previousIds: string[];
};

export type EventCache = {
  errors: ErrorResult[];
  getLogEntryEdge: (continuationToken: string) => LogEntryEdge | null;
  paginatedData: PaginatedData; // note that the caller must use a special parameter doGetPaginatedData for performance reasons
  results: EventCacheData[];
};

/**
 * Use this hook to subscribe to data in the event cache from UI components; callers will render when the underlying
 * data changes. Callers can subscribe to collections of raw data or pre-formatted data collections such as seriesData
 * for any number of event query ids. This hook guarantees a stable identity for data that has not changed since the
 * previous render.
 *
 * @param ids                   list of unique identifiers for the event cache generated from the parameters for each query
 * @param doGetPaginatedData    if true, the caller needs paginatedData, which must be specified for performance reasons
 */
export const useEventCache = (ids: string[] = [], doGetPaginatedData = false): EventCache => {

  const client = useApolloClient();
  const activeSearchMatchCount = useRecoilValue(activeSearchMatchCountState);
  const cacheWatcher = useRecoilValue(eventCacheWatcher);
  const errorsById = useRecoilValue(lrqErrorsById);
  const errors = ids.map((id) => errorsById[id]);
  const sortOrder = useRecoilValue(eventSortOrderState);

  // Track a stable reference to this hook's return value, and return that reference if it has been initialized and none
  // of the relevant dependencies have changed; the Apollo cache, the errors global, or the ids parameter supplied to
  // the hook, etc.
  // TODO: Return stable references for the entire tree {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4468}
  const stableEventCache = useRef<EventCache | null>(null);
  const activeSearchMatchCountChanged = useDelta(activeSearchMatchCount);
  const cacheChanged = useDelta(cacheWatcher);
  const errorsChanged = useDelta(errorsById);
  const sortOrderChanged = useDelta(sortOrder);
  const idsChanged = useDelta(ids, { deep: true });
  if (!activeSearchMatchCountChanged && !cacheChanged && !errorsChanged && !sortOrderChanged && !idsChanged && stableEventCache?.current) {
    return stableEventCache.current as EventCache;
  }

  /**
   * Returns a LogEntryEdge from the Apollo cache, where they are normalized by continuationToken
   *
   * @param continuationToken        the unique identifier for a log entry
   *
   * @returns        The LogEntryEdge from the cache with this continuationToken or null
   */
  const getLogEntryEdge = (
    continuationToken: string,
  ): LogEntryEdge | null => client.readFragment({
    id: 'LogEntryEdge:' + JSON.stringify({ continuationToken }),
    fragment: generateLoglineNode(),
  });

  // Map each id to the EventCacheData representation of its query's results. The id is used to look up the matching
  // QueryRequestResult from the cache (which is normalized by id) with the readFragment method from Apollo client.
  const results = ids.map((id): EventCacheData => {

    const queryVariables = (id && JSON.parse(id)) || {} as EventQueryInput;

    /*
    IMPORTANT: when fetching newly-generated graphql fields, make sure they've been added to:
      1) whatever graphql fragment you're using to fetch the data
      2) the data field, below
      3) the data stanza in generateAllDataNode()
    */

    // readFragment is not a hook; it can be called in a loop and the caller does not render when the result changes
    const queryRequestResult = client.readFragment({
      id: 'QueryRequestResult:' + id,
      fragment: generateAllDataNode(),
    });

    const { data, stepsCompleted, totalSteps } = queryRequestResult || {};
    const isDone = totalSteps > 0 && stepsCompleted === totalSteps;
    const {
      matchCount,
      outcome,
      resolvedTimeRange,
      facets,
      sampledEventCount,
      matches,
      plots,
      sliceInfo,
      xAxis,
      columns,
      keyColumns,
      omittedEvents,
      partialResultsDueToTimeLimit,
      values,
      cells,
      status,
      warnings,
      max,
      median,
      mean,
      min,
      p10,
      p90,
      p99,
      p999,
      positiveSampleCounts,
      positiveXAxis,
      zeroCount,
      bucketRatio,
      maxNegative,
      maxPositive,
      minNegative,
      minPositive,
      negativeBucketWidths,
      negativeSampleCounts,
      negativeXAxis,
      positiveBucketWidths,
    } = data || {};

    const facetData = {
      matchCount,
      outcome,
      resolvedTimeRange,
      facets,
      sampledEventCount,
    };

    const histogramData = {
      bucketRatio,
      matchCount,
      max,
      maxNegative,
      maxPositive,
      mean,
      median,
      facets,
      min,
      minNegative,
      minPositive,
      negativeBucketWidths,
      negativeSampleCounts,
      negativeXAxis,
      outcome,
      resolvedTimeRange,
      p10,
      p90,
      p99,
      p999,
      positiveBucketWidths,
      positiveSampleCounts,
      positiveXAxis,
      zeroCount,
    };

    /**
     * Previously we relied on the server to return the isAtStart and isAtEnd properties.
     * However:
     *   1. The values for these properties were not accurate for RUNNING queries
     *   2. The values were derived from data already available on the client
     *
     * We now calculate these on the client, but still store the values as part of logEntryData.
     * If the matches returned by the server are fewer than the number requested and the query is complete, then either
     * isAtEnd or isAtStart is true, depending on the direction of the query. Otherwise they are false.
     **/
    const { ascending, numResults } = queryVariables;
    const isAtBoundary = isDone && matches?.length < numResults;
    const isAtStart = isAtBoundary && !ascending;
    const isAtEnd = isAtBoundary && ascending;
    const logEntryData = {
      matchCount,
      outcome,
      resolvedTimeRange,
      isAtEnd,
      isAtStart,
      matches,
    };

    const plotData = {
      matchCount,
      outcome,
      resolvedTimeRange,
      plots,
      sliceInfo,
      xAxis,
    };

    const pqResultData = {
      columns,
      keyColumns,
      matchCount,
      outcome,
      resolvedTimeRange,
      omittedEvents,
      partialResultsDueToTimeLimit,
      values,
      cells,
      status,
      warnings,
    };

    const yAxis = plots?.[0]?.samples;
    // Don't attempt to zip arrays of unequal length
    const isValidSeries = xAxis?.length > 0 && xAxis?.length === yAxis?.length;
    // TODO: Ideally this could support progressive data stitching and progressive rendering, but for now partial series
    // data won't be returned
    const seriesData = isValidSeries && isDone ? {
      matchCount,
      // TODO-perf FRONT-4468: zip always returns a new array reference, thus causing redundant renders in consuming compoenents and effects
      series: zip(xAxis, yAxis),
    } : undefined;

    return {
      data,
      isDone,
      facetData,
      histogramData,
      logEntryData,
      plotData,
      pqResultData,
      queryVariables,
      seriesData,
    };
  });

  // paginatedData represents the combined pagination data of each id provided to the hook
  const paginatedData = doGetPaginatedData
    ? getCachedPaginatedData(results, activeSearchMatchCount, getLogEntryEdge, sortOrder)
    : { ...DEFAULT_PAGINATED_DATA };

  const eventCache = {
    errors,
    getLogEntryEdge,
    paginatedData,  // note that the caller must use a special parameter doGetPaginatedData for performance reasons
    results,
  };
  stableEventCache.current = eventCache;
  return eventCache;
};
